'use client';

import { Input } from '@ethsign/ui';
import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  Row,
  useReactTable
} from '@tanstack/react-table';
import { ReactNode, useEffect, useRef, useState } from 'react';

import { useClickAway } from 'react-use';

import { DataTablePagination } from '@/components/Table/Pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  interface TableMeta<TData extends Record<any, any>> {
    updateData: (rowIndex: number, column: TData) => void;
    editing: boolean;
    setEditing: (v: boolean) => void;
  }
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  interface ColumnMeta<TData extends Record<any, any>> {
    renderFormItem?: ({
      value,
      onChange,
      rowData,
      setRowData
    }: {
      value: string;
      onChange: (v: string) => void;
      rowData: TData;
      setRowData: (v: TData) => void;
    }) => ReactNode;
    renderCell?: (rowData: TData) => ReactNode;
    validate?: (rowData: TData, index: number) => boolean;
    canEdit?: boolean; //default true
  }
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  onDataChange?: (data: TData[]) => void;
  className?: string;
  rowSelection?: { onRowSelect: (data: { [i: number]: boolean }) => void };
  pagination?: {
    pageSize: number;
    onChange?: (page: number) => void;
    total?: number;
    pageIndex?: number;
    manualPagination?: boolean;
  };
  emptyText?: ReactNode;
  getTable?: (table: any) => void;
}

export const EditCell = ({ row, table, column }: { table: any; row: Row<any>; column: Column<any> }) => {
  const columnMeta = column.columnDef.meta;
  const tableMeta = table.options.meta;
  const [record, setRecord] = useState(row.original);

  useEffect(() => {
    setRecord(row.original);
  }, [row.original]);

  const [isEditing, setIsEditing] = useState(false);

  const ref = useRef(null);

  const setRowData = (index: number, rowData: Record<string, any>) => {
    tableMeta?.updateData(index, rowData);
  };

  const onSave = () => {
    console.log('SAVING', row.index, column.id, record[column.id]);
    if (columnMeta?.validate && !columnMeta?.validate(record, row.index)) return;
    setRowData(row.index, record);
    setIsEditing(false);
    tableMeta.setEditing(false);
  };

  useClickAway(ref, () => {
    console.log('OUTSIDE CLICKED');
    if (!isEditing) return;
    onSave();
  });

  const updateRecord = (rowData: Record<string, any>) => {
    setRecord({
      ...record,
      ...rowData
    });
  };

  const setValue = (v: string) => {
    updateRecord({
      [column.id]: v
    });
  };

  const handleClick = () => {
    // 不可编辑
    if (columnMeta?.canEdit === false) return;
    // 其他单元格正在编辑
    if (tableMeta.editing && !isEditing) return;
    setIsEditing(true);
    tableMeta.setEditing(true);
  };

  return (
    <div className={'w-full h-full'}>
      <div onClick={handleClick}>
        {columnMeta?.renderCell ? columnMeta?.renderCell(row.original) : record[column.id]}
      </div>
      {isEditing ? (
        <div
          ref={ref}
          className={
            'absolute top-0 left-0 w-full h-full rounded-md bg-primary-25 shadow-md border border-primary-300 [&>*]:h-full'
          }
        >
          {columnMeta?.renderFormItem ? (
            columnMeta?.renderFormItem({
              value: record[column.id],
              onChange: setValue,
              setRowData: updateRecord,
              rowData: record
            })
          ) : (
            <Input
              className={'shadow-none h-full'}
              value={record[column.id]}
              onChange={(e) => setValue(e.target.value)}
            />
          )}
        </div>
      ) : null}
    </div>
  );
};

export function EditableTable<TData, TValue>({
  columns,
  pagination,
  data,
  className,
  rowSelection,
  emptyText,
  getTable,
  onDataChange
}: DataTableProps<TData, TValue>) {
  const [selectedData, setSelectedData] = useState({});
  const [paginationInfo, setPaginationInfo] = useState<any>({
    ...(pagination || {})
  });
  const [editing, setEditing] = useState(false);

  const table = useReactTable({
    data,
    defaultColumn: {
      cell: EditCell
    } as Partial<ColumnDef<TData>>,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: !pagination || pagination?.manualPagination ? undefined : getPaginationRowModel(),
    onRowSelectionChange: setSelectedData,
    state: {
      rowSelection: selectedData,
      pagination: pagination ? paginationInfo : undefined
    },
    onPaginationChange: setPaginationInfo,
    pageCount: pagination?.manualPagination ? Math.ceil((pagination?.total || 0) / pagination?.pageSize) : undefined,
    manualPagination: pagination?.manualPagination,
    meta: {
      updateData: (rowIndex: number, column: TData) => {
        onDataChange?.(
          data.map((row, index) => {
            if (index === rowIndex) {
              return {
                ...data[rowIndex]!,
                ...column
              };
            }
            return row;
          })
        );
      },
      editing,
      setEditing
    },
    debugTable: true
  });

  useEffect(() => {
    rowSelection?.onRowSelect(selectedData);
  }, [selectedData]);
  useEffect(() => {
    pagination?.onChange && pagination?.onChange(paginationInfo.pageIndex + 1);
  }, [paginationInfo]);
  useEffect(() => {
    if (pagination?.pageIndex == undefined) return;
    setPaginationInfo({ pageIndex: pagination?.pageIndex, pageSize: pagination?.pageSize });
  }, [pagination?.pageIndex]);
  useEffect(() => {
    getTable && getTable(table);
  }, [table]);

  return (
    <div className={cn('bg-white w-full overflow-hidden', className)}>
      <Table containerClassName={'overflow-visible'}>
        <TableHeader className="bg-gray-25 [&_tr]:divide-x [&_tr]:divide-gray-100 text-gray-500">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header, i) => {
                return (
                  <TableHead key={i}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, i) => (
              <TableRow key={i} data-state={row.getIsSelected() && 'selected'}>
                {row.getVisibleCells().map((cell, j) => (
                  <TableCell key={j} className={'relative'}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center text-gray-400 opacity-60 text-xs">
                <div className="flex items-center justify-center w-full">{emptyText || 'No results.'}</div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {pagination && (
        <div className="w-full">
          <DataTablePagination table={table} />
        </div>
      )}
    </div>
  );
}
